import { Button, Grid, ScrollView, SearchField } from "@aws-amplify/ui-react";
import { useMemo, useState } from "react";
import LocalizedStrings from "react-localization";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import demoWarehouseMaintenance from "config/demo/warehouseMaintenance.json";
import WarehouseMaintenanceForm from "components/agro/warehouseMaintenance/WarehouseMaintenanceForm";

const WarehouseMaintenance = () => {
  const [items] = useState((demoWarehouseMaintenance && demoWarehouseMaintenance.slice) ? demoWarehouseMaintenance.slice(0, 10) : []);
  const [itemDetails, setItemDetails] = useState();
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    itemId: null,
    itemName: null,
  });
  const [modalForm, setModalForm] = useState({
    visible: false,
    itemId: null,
    action: null,
  });

  const columnsMapped = useMemo(
    () =>
      items.map((it) => ({
        ...it,
        actions: (
          <>
            <ActionIcon
              name={itemDetails?.id === it.id ? strings.maintenance.hide : strings.maintenance.show}
              icon={itemDetails?.id === it.id ? "VisibilityOff" : "Visibility"}
              color="primary"
              onClick={() => {
                itemDetails?.id === it.id ? setItemDetails(null) : setItemDetails(it);
              }}
            />
            <ActionIcon
              name={strings.maintenance.edit}
              icon="Edit"
              color="primary"
              onClick={() => openModal("update", it.id)}
            />
            <ActionIcon
              name={strings.maintenance.delete}
              icon="Delete"
              color="red.60"
              onClick={() =>
                setModalDetails({
                  visible: true,
                  itemId: it.id,
                  itemName: it.warehouseName || it.id,
                })
              }
            />
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, itemDetails?.id]
  );

  const openModal = (action, id = null) => setModalForm({ visible: true, action, itemId: id });

  const tableColumns = [
    { key: "date", label: strings.table.headers.date, _style: { width: "20%" } },
    { key: "farmId", label: strings.table.headers.farm, _style: { width: "20%" } },
    { key: "warehouseId", label: strings.table.headers.warehouse, _style: { width: "20%" } },
    { key: "performedById", label: strings.table.headers.performedBy, _style: { width: "20%" } },
    { key: "actions", label: "", _style: { width: "20%" } },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      <Button
        style={styles.buttonAdd}
        children={strings.maintenance.add}
        variation="primary"
        onClick={() => openModal("create")}
      />
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <SearchField label={strings.table.search.label} placeholder={strings.table.search.placeholder} />
        </Grid>

        <CTable
          className="table-centered"
          items={columnsMapped}
          columns={tableColumns}
          tableHeadProps={{ color: "light" }}
          responsive
          hover
        />

        <CCollapse visible={!!itemDetails?.id} style={styles.collapseCard}>
          <CCard key={itemDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">{strings.details.title} "{itemDetails?.warehouseName || itemDetails?.id}"</h4>

              <p className="text-muted"><b>{strings.details.date}:</b> {itemDetails?.date ? moment(itemDetails.date).format("YYYY-MM-DD") : "-"}</p>
              <p className="text-muted"><b>{strings.details.farm}:</b> {itemDetails?.farmName ?? itemDetails?.farmId ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.warehouse}:</b> {itemDetails?.warehouseName ?? itemDetails?.warehouseId ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.protocolDescription}:</b> {itemDetails?.protocolDescription ?? "-"}</p>
              <p className="text-muted"><b>{strings.details.performedBy}:</b> {itemDetails?.performedByName ?? itemDetails?.performedById ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.observations}:</b> {itemDetails?.observations ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.photoEvidence}:</b> {(Array.isArray(itemDetails?.photoEvidence) ? itemDetails.photoEvidence.join(", ") : itemDetails?.photoEvidence) ?? "-"}</p>

              <p className="text-muted"><b>{strings.details.createdAt}:</b> {itemDetails?.createdAt ? moment(itemDetails.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}</p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>

      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, { name: modalDetails.itemName })}
        onCancel={() => setModalDetails({ visible: false, itemId: null })}
      />

      {modalForm.action && (
        <CModal size="xl" alignment="center" visible={modalForm.visible} onClose={() => setModalForm({ visible: false })} backdrop="static">
          <CModalHeader>
            <CModalTitle>{modalForm.action === "create" ? strings.modal.addTitle : strings.modal.updateTitle}</CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              <WarehouseMaintenanceForm
                type={modalForm.action}
                initialValues={modalForm.action === "update" ? items.find((i) => i.id === modalForm.itemId) || {} : {}}
                onSubmit={() => {}}
                onCancel={() => setModalForm({ visible: false })}
              />
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Mantenimiento de Almacenes",
    maintenance: {
      add: "Registrar Mantenimiento de Almacén",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    table: {
      headers: {
        date: "Fecha",
        farm: "Finca",
        warehouse: "Almacén",
        performedBy: "Persona que realiza",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles Mantenimiento Almacén",
      date: "Fecha",
      farm: "Finca",
      warehouse: "Almacén",
      protocolDescription: "Protocolo de mantenimiento",
      performedBy: "Persona que realiza",
      observations: "Observaciones",
      photoEvidence: "Evidencia fotográfica",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el mantenimiento {name}?",
      addTitle: "Registrar Mantenimiento de Almacén",
      updateTitle: "Actualizar Mantenimiento de Almacén",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  collapseCard: { marginTop: "15px" },
};

export default WarehouseMaintenance;
